import React from "react";
import { Link } from "react-router-dom";
import { Shield } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";

const PolitiqueConfidentialite = () => {
  return (
    <div className="font-sans min-h-screen bg-gray-50">
      <Header />

      {/* Hero Section */}
      <section className="bg-gradient-to-r from-black to-flowkraft-primary text-white py-20">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-center mb-8">
            <Shield className="w-16 h-16 text-flowkraft-secondary" />
          </div>
          <h1 className="text-4xl md:text-6xl font-bold text-center mb-6">
            Politique de Confidentialité
          </h1>
          <p className="text-xl text-center text-gray-200 max-w-3xl mx-auto">
            Protection de vos données personnelles
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-4xl">

          {/* Introduction */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Introduction</h2>
            <p className="text-gray-700 mb-4">
              FlowKraft Agency s'engage à protéger la confidentialité et la sécurité de vos données personnelles.
              Cette politique de confidentialité explique comment nous collectons, utilisons, partageons et
              protégeons vos informations personnelles conformément au Règlement Général sur la Protection
              des Données (RGPD) et à la loi "Informatique et Libertés".
            </p>
          </div>

          {/* Responsable du traitement */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">1. Responsable du traitement des données</h2>
            <div className="text-gray-700 space-y-2">
              <p><strong>Responsable :</strong> FlowKraft Agency</p>
              <p><strong>Adresse :</strong> Toulouse, France</p>
              <p><strong>Email :</strong> <a href="mailto:info@flowkraftagency.com" className="text-flowkraft-primary hover:underline">info@flowkraftagency.com</a></p>
              <p><strong>Téléphone :</strong> <a href="tel:+33652794562" className="text-flowkraft-primary hover:underline">+33 (0)6 52 79 45 62</a></p>
            </div>
          </div>

          {/* Données collectées */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">2. Données personnelles collectées</h2>
            <p className="text-gray-700 mb-4">
              Nous pouvons collecter les types de données personnelles suivants :
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li><strong>Données d'identification :</strong> nom, prénom, adresse email</li>
              <li><strong>Données de contact :</strong> numéro de téléphone, adresse postale</li>
              <li><strong>Données professionnelles :</strong> nom de l'entreprise, fonction, secteur d'activité</li>
              <li><strong>Données de navigation :</strong> adresse IP, type de navigateur, pages visitées, durée de visite</li>
              <li><strong>Données de communication :</strong> messages envoyés via nos formulaires de contact</li>
            </ul>
          </div>

          {/* Finalités */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">3. Finalités du traitement</h2>
            <p className="text-gray-700 mb-4">
              Vos données personnelles sont collectées et traitées pour les finalités suivantes :
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Répondre à vos demandes de contact et d'information</li>
              <li>Gérer la relation client et le suivi de projets</li>
              <li>Établir des devis et des contrats</li>
              <li>Améliorer nos services et votre expérience utilisateur</li>
              <li>Vous envoyer des informations sur nos services (avec votre consentement)</li>
              <li>Respecter nos obligations légales et réglementaires</li>
              <li>Analyser et optimiser notre site web</li>
            </ul>
          </div>

          {/* Base légale */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">4. Base légale du traitement</h2>
            <p className="text-gray-700 mb-4">
              Le traitement de vos données personnelles repose sur les bases légales suivantes :
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li><strong>Exécution d'un contrat :</strong> pour gérer votre commande ou prestation</li>
              <li><strong>Consentement :</strong> pour l'envoi de communications marketing</li>
              <li><strong>Intérêt légitime :</strong> pour améliorer nos services et assurer la sécurité de notre site</li>
              <li><strong>Obligation légale :</strong> pour respecter nos obligations comptables et fiscales</li>
            </ul>
          </div>

          {/* Durée de conservation */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">5. Durée de conservation des données</h2>
            <p className="text-gray-700 mb-4">
              Vos données personnelles sont conservées pour la durée nécessaire aux finalités pour lesquelles
              elles sont traitées :
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li><strong>Données clients :</strong> pendant la durée de la relation contractuelle et jusqu'à 3 ans après la fin de celle-ci</li>
              <li><strong>Données prospects :</strong> pendant 3 ans à compter du dernier contact</li>
              <li><strong>Données comptables :</strong> 10 ans conformément aux obligations légales</li>
              <li><strong>Cookies et données de navigation :</strong> 13 mois maximum</li>
            </ul>
          </div>

          {/* Destinataires */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">6. Destinataires des données</h2>
            <p className="text-gray-700 mb-4">
              Vos données personnelles peuvent être transmises aux catégories de destinataires suivantes :
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Personnel autorisé de FlowKraft Agency</li>
              <li>Prestataires techniques (hébergement, maintenance, développement)</li>
              <li>Services de paiement sécurisé</li>
              <li>Autorités légales et réglementaires sur demande</li>
            </ul>
            <p className="text-gray-700 mt-4">
              Nous nous assurons que tous nos partenaires respectent la confidentialité et la sécurité de vos données.
            </p>
          </div>

          {/* Transferts */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">7. Transferts de données hors UE</h2>
            <p className="text-gray-700">
              Vos données peuvent être transférées vers des pays situés hors de l'Union Européenne, notamment
              pour l'hébergement de notre site (Vercel, États-Unis). Ces transferts sont encadrés par des garanties
              appropriées conformément au RGPD (clauses contractuelles types, Privacy Shield, etc.).
            </p>
          </div>

          {/* Sécurité */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">8. Sécurité des données</h2>
            <p className="text-gray-700 mb-4">
              Nous mettons en œuvre des mesures techniques et organisationnelles appropriées pour protéger
              vos données personnelles contre :
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>L'accès non autorisé</li>
              <li>La perte, la destruction ou l'altération accidentelle</li>
              <li>La divulgation ou l'utilisation illicite</li>
            </ul>
            <p className="text-gray-700 mt-4">
              Ces mesures incluent notamment le chiffrement SSL/TLS, des sauvegardes régulières, des contrôles
              d'accès stricts et la sensibilisation de nos équipes.
            </p>
          </div>

          {/* Droits */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">9. Vos droits</h2>
            <p className="text-gray-700 mb-4">
              Conformément au RGPD, vous disposez des droits suivants concernant vos données personnelles :
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li><strong>Droit d'accès :</strong> obtenir une copie de vos données personnelles</li>
              <li><strong>Droit de rectification :</strong> corriger vos données inexactes ou incomplètes</li>
              <li><strong>Droit à l'effacement :</strong> demander la suppression de vos données</li>
              <li><strong>Droit à la limitation :</strong> limiter le traitement de vos données</li>
              <li><strong>Droit d'opposition :</strong> vous opposer au traitement de vos données</li>
              <li><strong>Droit à la portabilité :</strong> recevoir vos données dans un format structuré</li>
              <li><strong>Droit de retirer votre consentement :</strong> à tout moment</li>
              <li><strong>Droit de définir des directives :</strong> concernant le sort de vos données après votre décès</li>
            </ul>
            <p className="text-gray-700 mt-4">
              Pour exercer vos droits, contactez-nous à :{" "}
              <a href="mailto:info@flowkraftagency.com" className="text-flowkraft-primary hover:underline">
                info@flowkraftagency.com
              </a>
            </p>
            <p className="text-gray-700 mt-4">
              Vous disposez également du droit d'introduire une réclamation auprès de la CNIL (Commission Nationale
              de l'Informatique et des Libertés) si vous estimez que vos droits ne sont pas respectés.
            </p>
          </div>

          {/* Cookies */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">10. Cookies et technologies similaires</h2>
            <p className="text-gray-700 mb-4">
              Notre site utilise des cookies pour améliorer votre expérience de navigation. Les cookies sont
              de petits fichiers texte stockés sur votre appareil.
            </p>
            <p className="text-gray-700 mb-4">
              <strong>Types de cookies utilisés :</strong>
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li><strong>Cookies essentiels :</strong> nécessaires au fonctionnement du site</li>
              <li><strong>Cookies analytiques :</strong> pour comprendre comment vous utilisez notre site</li>
              <li><strong>Cookies de performance :</strong> pour améliorer les performances du site</li>
            </ul>
            <p className="text-gray-700 mt-4">
              Vous pouvez gérer vos préférences en matière de cookies via les paramètres de votre navigateur.
            </p>
          </div>

          {/* Modifications */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">11. Modifications de la politique</h2>
            <p className="text-gray-700">
              Nous nous réservons le droit de modifier cette politique de confidentialité à tout moment.
              Toute modification sera publiée sur cette page avec une date de mise à jour actualisée.
              Nous vous encourageons à consulter régulièrement cette page pour prendre connaissance
              des éventuelles modifications.
            </p>
          </div>

          {/* Contact */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">12. Contact</h2>
            <p className="text-gray-700">
              Pour toute question concernant cette politique de confidentialité ou le traitement de vos données
              personnelles, vous pouvez nous contacter :
            </p>
            <div className="text-gray-700 space-y-2 mt-4">
              <p><strong>Email :</strong> <a href="mailto:info@flowkraftagency.com" className="text-flowkraft-primary hover:underline">info@flowkraftagency.com</a></p>
              <p><strong>Téléphone :</strong> <a href="tel:+33652794562" className="text-flowkraft-primary hover:underline">+33 (0)6 52 79 45 62</a></p>
              <p><strong>Adresse :</strong> Toulouse, France</p>
            </div>
          </div>

          {/* Date de mise à jour */}
          <div className="text-gray-600 text-sm italic">
            <p>Dernière mise à jour : {new Date().toLocaleDateString('fr-FR')}</p>
          </div>

        </div>
      </section>

      {/* CTA */}
      <section className="relative py-20 overflow-hidden">
        {/* Background Image */}
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: "url(/images/main/2149755776.jpg)" }}
        ></div>
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-flowkraft-primary to-black opacity-90"></div>

        <div className="container mx-auto px-4 text-center relative z-10">
          <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">
            Vos données sont en sécurité
          </h2>
          <p className="text-xl mb-8 text-gray-200">
            Nous nous engageons à protéger votre vie privée
          </p>
          <Link
            to="/#contact"
            className="inline-block bg-white text-flowkraft-primary px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors"
          >
            Nous contacter
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PolitiqueConfidentialite;
